<script setup lang="ts">
import { InterfaceFile } from "~/classes/file/InterfaceFile"

const props = defineProps({
  reads: null
})

const { getFileReference } = useAgent()
const firstRead = computed(() => props.reads.entries().next().value?.[1])
const collapsed = ref(true)

function fileWithFallback(item: {
  request: FileDownloadRequestDatagram
  response?: FileDownloadResponseDatagram
}): InterfaceFile {
  const reference = getFileReference(item.request.file.fileId)
  if (reference)
    return reference
  if (item.response?.file)
    return new InterfaceFile(item.response.file)
  return new InterfaceFile(item.request.file)
}
</script>

<template>
  <div class="container">
    <div class="label">
      Reading files
    </div>
    <div
      v-if="collapsed && reads.size > 1 && firstRead"
      class="teaser row"
      @click="collapsed = !collapsed"
    >
      <file-inline
        :file="fileWithFallback(firstRead)"
        selectable
        location="agent"
      />
      <div class="more">
        +{{ reads.size - 1 }} more
      </div>
    </div>
    <template v-else-if="!collapsed || reads.size <= 1">
      <div
        v-for="[key, item] in reads"
        :key="key"
        class="row"
      >
        <file-inline
          :file="fileWithFallback(item)"
          selectable
          location="agent"
        />
        <div v-if="item.response?.error" class="status">
          <icon-action
            type="cross"
            size="2xs"
            class="cross"
          />
        </div>
         <div v-else-if="item.response?.file" class="status">
          <icon-action
            type="check"
            size="2xs"
            class="check"
          />
        </div>
        <div v-else class="spinner">
          <interface-spinner class="inner" />
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.container
  @apply flex flex-col
  @apply outline outline-0.5 outline-$global-violet-secondary bg-$global-violet-accent
  @apply w-50 max-w-full rounded-2 p-[#{$panel-agent-px-step}]
  @apply t-panel-sm

.label
  @apply px-[#{$panel-agent-px-step-item}] py-[3px]

.row
  @apply flex items-center gap-[#{$panel-agent-gap-step-item}]
  @apply px-[#{$panel-agent-px-step-item}] py-[3px]
  @apply t-panel-xs

.more
  @apply flex-none
  @apply bg-$global-steel-accent
  @apply text-[#{$global-t-secondary}]
  @apply p-[3px] rounded-1

.status
  @apply flex-none flex items-center

.check
  @apply text-$global-green-primary
.cross
  @apply text-$global-red-primary

.spinner
  @apply w-3 h-3 flex items-center justify-center
.inner
  @apply w-2 h-2 text-$global-t-secondary
</style>
